// Loads the user's roles + permissions ONCE on app init and stores them in
// useState('user-access'). Replaces the previous behaviour of stuffing them
// into the session cookie — that pushed the cookie past the 4096 char browser
// limit for users with many permissions, causing Set-Cookie to be silently
// ignored → user effectively logged out.
//
// Runs on BOTH server (SSR) and client. This is deliberate: route middleware
// like `permission` reads usePermissions() (→ this state), and on a hard
// refresh / direct URL that middleware executes on the SERVER. If the state
// were empty during SSR (as it was when this plugin was client-only), the
// permission check would wrongly 403 on refresh even though the user has the
// permission. Populating on the server fixes that AND avoids the hydration
// mismatch — server and client now render from the same access state.
//
// Cookie forwarding: plain $fetch on the server does NOT carry the incoming
// request's session cookie, so /api/auth/me would see no session → 401 → empty
// state (the original bug that made someone make this plugin client-only).
// useRequestFetch() forwards the request headers (incl. cookie) during SSR; on
// the client it's just $fetch.
export default defineNuxtPlugin(async () => {
  const access = useState<{ roles: string[]; permissions: string[] }>('user-access', () => ({ roles: [], permissions: [] }))
  // Skip if already populated (e.g. client hydrating from the SSR payload).
  if (access.value.permissions.length || access.value.roles.length) return

  const { loggedIn } = useUserSession()
  if (!loggedIn.value) return

  try {
    const request = useRequestFetch()
    const data = await request<{ roles?: string[]; permissions?: string[] }>('/api/auth/me')
    access.value = {
      roles:       Array.isArray(data?.roles)       ? data.roles       : [],
      permissions: Array.isArray(data?.permissions) ? data.permissions : [],
    }
  } catch {
    // 401 atau gagal — biar kosong, auth middleware akan handle redirect.
  }
})
